import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { UserPlus, FileText, Truck, ArrowRight, Heart, Users, Package } from 'lucide-react';
import '../../../assets/css/home/HowitWork.css';

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animations on mount
    setIsVisible(true);

    // Auto-rotate steps every 3 seconds
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      number: '01',
      icon: UserPlus,
      title: 'Register as Donor / NGO / Receiver',
      description: 'Create your account in minutes. Choose your role and start making a difference today.',
      color: '#1F7A7A',
      bgColor: 'rgba(31, 122, 122, 0.1)',
      features: ['Quick Sign Up', 'Secure Profile', 'Role Selection']
    },
    {
      number: '02',
      icon: FileText,
      title: 'Post Donation or Request',
      description: 'Share what you can donate or what you need. Add details, photos, and location information.',
      color: '#165C5C',
      bgColor: 'rgba(22, 92, 92, 0.1)',
      features: ['Easy Posting', 'Photo Upload', 'Location Tracking']
    },
    {
      number: '03',
      icon: Truck,
      title: 'Connect & Complete Delivery',
      description: 'Match with verified users, coordinate delivery, and track your impact in real-time.',
      color: '#FFB800',
      bgColor: 'rgba(255, 184, 0, 0.1)',
      features: ['Real-Time Matching', 'Delivery Tracking', 'Impact Reports']
    }
  ];

  const stats = [
    { icon: Users, value: '10,000+', label: 'Active Users' },
    { icon: Heart, value: '25,000+', label: 'Donations Made' },
    { icon: Package, value: '95%', label: 'Success Rate' }
  ];

  return (
    <section className="how-it-works-section">
      <Container>
        {/* Header Section */}
        <div className="text-center mb-5">
          <div 
            className={`badge-custom mb-3 ${isVisible ? 'fade-in-up' : ''}`}
            style={{ animationDelay: '0s' }}
          >
            <Heart size={20} className="me-2" />
            HOW IT WORKS
          </div>
          <h2 
            className={`main-heading ${isVisible ? 'fade-in-up' : ''}`}
            style={{ animationDelay: '0.2s' }}
          >
            Simple Steps to Make a Difference
          </h2>
          <p 
            className={`sub-heading ${isVisible ? 'fade-in-up' : ''}`}
            style={{ animationDelay: '0.4s' }}
          >
            Join thousands of users making an impact. Start your journey in just 3 easy steps.
          </p>
        </div>

        {/* Steps Section */}
        <Row className="g-4 mb-5">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const isActive = activeStep === index;

            return (
              <Col lg={4} md={12} key={index}>
                <div
                  className={`step-card ${isActive ? 'active' : ''} ${isVisible ? 'fade-in-scale' : ''}`}
                  style={{ 
                    animationDelay: `${0.6 + index * 0.2}s`,
                    backgroundColor: isActive ? step.bgColor : '#FFFFFF',
                    borderColor: isActive ? step.color : 'rgba(31, 122, 122, 0.1)'
                  }}
                  onMouseEnter={() => setActiveStep(index)}
                >
                  {/* Step Number */}
                  <div 
                    className="step-number"
                    style={{ color: isActive ? step.color : '#165C5C' }}
                  >
                    {step.number}
                  </div>

                  {/* Icon Circle */}
                  <div
                    className="icon-circle"
                    style={{
                      backgroundColor: isActive ? step.color : step.bgColor
                    }}
                  >
                    <IconComponent 
                      size={40} 
                      style={{ color: isActive ? '#FFFFFF' : step.color }}
                    />
                  </div>

                  {/* Content */}
                  <h3 
                    className="step-title"
                    style={{ color: isActive ? step.color : '#165C5C' }}
                  >
                    {step.title}
                  </h3>
                  <p className="step-description">{step.description}</p>

                  {/* Features */}
                  <div className="features-container">
                    {step.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="feature-badge"
                        style={{
                          backgroundColor: isActive ? `${step.color}20` : '#F8F9FA',
                          borderColor: isActive ? step.color : '#E9ECEF'
                        }}
                      >
                        <span 
                          className="dot"
                          style={{ backgroundColor: isActive ? step.color : '#6C757D' }}
                        />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Arrow (Desktop only) */}
                  {index < steps.length - 1 && (
                    <div className="arrow-container d-none d-lg-block">
                      <ArrowRight 
                        size={32} 
                        style={{ color: isActive ? step.color : '#E9ECEF' }}
                        className={isActive ? 'slide-animation' : ''}
                      />
                    </div>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>

        {/* Stats Section */}
        <Row className="g-4 mb-5">
          {stats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <Col lg={4} md={4} sm={12} key={index}>
                <div
                  className={`stat-card ${isVisible ? 'fade-in-up' : ''}`}
                  style={{ animationDelay: `${1.4 + index * 0.15}s` }}
                >
                  <div className="stat-icon">
                    <StatIcon size={32} />
                  </div>
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </Col>
            );
          })}
        </Row>

        {/* CTA Button */}
        <div 
          className={`text-center ${isVisible ? 'fade-in-up' : ''}`}
          style={{ animationDelay: '2s' }}
        >
          <button className="cta-button">
            Get Started Now
            <ArrowRight size={20} className="ms-2" />
          </button>
        </div>
      </Container>
    </section>
  );
};

export default HowItWorks;